import { useState, useRef } from "react";
import type { CSSProperties } from "react";
import type { Theme, FontPairing, BatchResult, BatchRow } from "@structurify/shared-types";
import { MonoLabel, QuietButton } from "../components/primitives/index.ts";
import { initUpload, putFile, getDocumentStatus, type ExtractedField } from "../api/upload.ts";

type FileStatus = "queued" | "processing" | "done";

interface StagedFile {
  name: string;
  size: string;
  file: File;
}

interface StageBulkProps {
  theme: Theme;
  fonts: FontPairing;
  session?: string | undefined;
  onComplete: (result: BatchResult) => void;
  onCancel: () => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function findField(fields: ExtractedField[], ...keywords: string[]): string {
  for (const kw of keywords) {
    const f = fields.find(
      (field) => field.label.toLowerCase().includes(kw) || field.name.toLowerCase().includes(kw),
    );
    if (f?.value) return f.value;
  }
  return "—";
}

function mapToRow(filename: string, fields: ExtractedField[], docType: string | null): BatchRow {
  const avgConf =
    fields.length > 0 ? fields.reduce((s, f) => s + f.confidence, 0) / fields.length : 0.5;
  return {
    file: filename,
    insured: findField(fields, "insured", "policyholder", "claimant", "name"),
    type: docType ?? findField(fields, "loss type", "claim type", "type", "category"),
    amount: findField(fields, "amount", "total", "loss amount", "claim amount"),
    date: findField(fields, "date", "loss date", "incident date", "claim date"),
    confidence: avgConf,
    flagged: avgConf < 0.85,
    failed: false,
  };
}

function failRow(filename: string, reason: string): BatchRow {
  return {
    file: filename,
    insured: "—",
    type: "—",
    amount: "—",
    date: "—",
    confidence: 0,
    flagged: false,
    failed: true,
    failReason: reason,
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function StageBulk({ theme, fonts, session, onComplete, onCancel }: StageBulkProps) {
  const [phase, setPhase] = useState<"select" | "processing">("select");
  const [files, setFiles] = useState<StagedFile[]>([]);
  const [progressByIdx, setProgressByIdx] = useState<Record<number, FileStatus>>({});
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const staged: StagedFile[] = Array.from(newFiles).map((f) => ({
      name: f.name,
      size: formatSize(f.size),
      file: f,
    }));
    setFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      return [...prev, ...staged.filter((f) => !existingNames.has(f.name))];
    });
  };

  const start = () => {
    setPhase("processing");
    const CONCURRENCY = 3;
    const results: (BatchRow | null)[] = Array(files.length).fill(null);
    const snapshot = files;
    let nextIdx = 0;

    const worker = async () => {
      while (nextIdx < snapshot.length) {
        const i = nextIdx++;
        const staged = snapshot[i];
        if (!staged) continue;
        setProgressByIdx((p) => ({ ...p, [i]: "processing" }));
        try {
          const { documentId, uploadUrl, token } = await initUpload(staged.file, session);
          await putFile(uploadUrl, token, staged.file);

          let done = false;
          for (let poll = 0; poll < 60 && !done; poll++) {
            await delay(2000);
            const status = await getDocumentStatus(documentId);
            if (status.status === "extracted" && status.fields) {
              results[i] = mapToRow(staged.file.name, status.fields, status.documentType);
              done = true;
            } else if (status.status === "failed") {
              results[i] = failRow(staged.file.name, status.error ?? "Extraction failed");
              done = true;
            }
          }
          if (!done) results[i] = failRow(staged.file.name, "Timed out");
        } catch (err) {
          results[i] = failRow(
            staged.file.name,
            err instanceof Error ? err.message : "Upload failed",
          );
        }
        setProgressByIdx((p) => ({ ...p, [i]: "done" }));
      }
    };

    void Promise.all(Array.from({ length: CONCURRENCY }, worker)).then(() => {
      const rows = results.map((r, i) => r ?? failRow(snapshot[i]?.name ?? "unknown", "Failed"));
      const failedCount = rows.filter((r) => r.failed).length;
      const flaggedCount = rows.filter((r) => !r.failed && (r.flagged || r.confidence < 0.85)).length;
      onComplete({
        workflow: "Bulk upload",
        total: snapshot.length,
        failed: failedCount,
        flagged: flaggedCount,
        rows,
      });
    });
  };

  if (phase === "select") {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 48px", overflow: "auto" }}>
        <div style={{ width: "100%", maxWidth: 880, opacity: 0, animation: "fadeUp 600ms forwards ease-out" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 18, marginBottom: 10 }}>
            <MonoLabel theme={theme} fonts={fonts} size={10}>BULK PROCESS</MonoLabel>
            <div style={{ flex: 1, height: 1, background: theme.rule }} />
            <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkMute}>UP TO 500 PER BATCH</MonoLabel>
          </div>

          <h2 style={{ fontFamily: fonts.sans, fontWeight: 500, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.025em", color: theme.ink, margin: "12px 0 14px 0" }}>
            Drop a folder of documents.
          </h2>
          <p style={{ fontFamily: fonts.sans, fontSize: 15, color: theme.inkSoft, lineHeight: 1.55, margin: 0, marginBottom: 24, maxWidth: 580 }}>
            Structurify will extract fields from each document and surface only the values that need a human eye.
          </p>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.webp,.docx"
            style={{ display: "none" }}
            onChange={(e) => addFiles(e.target.files)}
          />

          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            style={{ border: `1px dashed ${dragOver ? theme.ink : theme.ruleStrong}`, padding: "40px 32px", cursor: "pointer", textAlign: "center", marginBottom: 20, background: dragOver ? `${theme.ink}06` : "transparent", transition: "background 150ms, border-color 150ms" }}
          >
            <div style={{ fontFamily: fonts.sans, fontSize: 14, color: theme.ink, fontWeight: 500 }}>Drop documents or a folder here</div>
            <div style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, marginTop: 8, letterSpacing: "0.06em" }}>
              CLICK TO BROWSE · PDF · PNG · JPG · DOCX · UP TO 50MB EACH
            </div>
          </div>

          {files.length > 0 && (
            <div style={{ border: `1px solid ${theme.rule}`, marginBottom: 20, maxHeight: 240, overflow: "auto" }}>
              {files.map((f, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "20px 1fr auto auto", gap: 14, alignItems: "center", padding: "10px 14px", borderBottom: i < files.length - 1 ? `1px solid ${theme.rule}` : "none", background: theme.panel }}>
                  <svg width="14" height="14" viewBox="0 0 14 14">
                    <path d="M2 1 L9 1 L12 4 L12 13 L2 13 Z" fill="none" stroke={theme.inkMute} strokeWidth="1" />
                  </svg>
                  <span style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.ink }}>{f.name}</span>
                  <span style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute }}>{f.size}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, j) => j !== i)); }}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: theme.inkMute, fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.06em" }}
                  >×</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
            <span style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkMute }}>
              {files.length > 0 ? `${files.length} document${files.length > 1 ? "s" : ""} ready · ~${Math.ceil(files.length * 8)}s estimated` : " "}
            </span>
            <div style={{ display: "flex", gap: 10 }}>
              <QuietButton theme={theme} fonts={fonts} onClick={onCancel}>Cancel</QuietButton>
              <QuietButton theme={theme} fonts={fonts} primary disabled={files.length === 0} onClick={start}>
                Run batch
                <svg width="11" height="11" viewBox="0 0 11 11"><path d="M2 5.5 L9 5.5 M6 2.5 L9 5.5 L6 8.5" stroke="currentColor" strokeWidth="1" fill="none" /></svg>
              </QuietButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const doneCount = Object.values(progressByIdx).filter((s) => s === "done").length;
  const pct = files.length > 0 ? doneCount / files.length : 0;

  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px" }}>
      <div style={{ width: "100%", maxWidth: 720, opacity: 0, animation: "fadeUp 400ms forwards" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 18, marginBottom: 10 }}>
          <MonoLabel theme={theme} fonts={fonts} size={10}>RUNNING</MonoLabel>
          <div style={{ flex: 1, height: 1, background: theme.rule }} />
          <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkMute}>{doneCount}/{files.length} DONE</MonoLabel>
        </div>

        <h2 style={{ fontFamily: fonts.sans, fontWeight: 500, fontSize: 32, lineHeight: 1.1, letterSpacing: "-0.025em", color: theme.ink, margin: "12px 0 24px 0" }}>
          Processing {files.length} documents…
        </h2>

        <div style={{ height: 1, background: theme.rule, marginBottom: 20, position: "relative" }}>
          <div style={{ position: "absolute", left: 0, top: -1, height: 3, background: theme.ink, width: `${pct * 100}%`, transition: "width 320ms ease" }} />
        </div>

        <div style={{ border: `1px solid ${theme.rule}`, maxHeight: 320, overflow: "auto" }}>
          {files.map((f, i) => {
            const status = progressByIdx[i];
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "20px 1fr auto", gap: 12, alignItems: "center", padding: "8px 14px", borderBottom: i < files.length - 1 ? `1px solid ${theme.rule}` : "none", background: theme.panel, opacity: status === "done" ? 1 : status === "processing" ? 0.95 : 0.45, transition: "opacity 200ms" }}>
                <span style={{ fontFamily: fonts.mono, fontSize: 10, color: status === "done" ? theme.ink : theme.inkMute, letterSpacing: "0.04em" }}>
                  {status === "done" ? "✓" : status === "processing" ? "◔" : "·"}
                </span>
                <span style={{ fontFamily: fonts.sans, fontSize: 12.5, color: theme.ink }}>{f.name}</span>
                <span style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, letterSpacing: "0.06em" }}>
                  {status === "done" ? "EXTRACTED" : status === "processing" ? "READING…" : "QUEUED"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ----- StageBatchResult -----

interface StageBatchResultProps {
  theme: Theme;
  fonts: FontPairing;
  batch: BatchResult | null;
  onReviewQueue: () => void;
  onExport: () => void;
  onIntelligence?: () => void;
  onRowClick?: (row: BatchRow, idx: number) => void;
}

function thStyle(theme: Theme, fonts: FontPairing): CSSProperties {
  return {
    textAlign: "left",
    padding: "12px 14px",
    fontFamily: fonts.mono,
    fontSize: 9,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.inkMute,
    fontWeight: 500,
    borderBottom: `1px solid ${theme.rule}`,
  };
}

const tdStyle: CSSProperties = { padding: "12px 14px", verticalAlign: "middle" };

export function StageBatchResult({ theme, fonts, batch, onReviewQueue, onExport, onIntelligence, onRowClick }: StageBatchResultProps) {
  if (!batch) return null;
  const flaggedRows = batch.rows.filter((r) => r.flagged || r.confidence < 0.85);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "32px 48px 20px", flexShrink: 0 }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", opacity: 0, animation: "fadeUp 500ms forwards" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 18, marginBottom: 10 }}>
            <MonoLabel theme={theme} fonts={fonts} size={10}>BATCH COMPLETE</MonoLabel>
            <div style={{ flex: 1, height: 1, background: theme.rule }} />
            <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkMute}>{batch.workflow.toUpperCase()}</MonoLabel>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 48, marginTop: 14 }}>
            <div>
              <h2 style={{ fontFamily: fonts.sans, fontWeight: 500, fontSize: 32, lineHeight: 1.05, letterSpacing: "-0.025em", color: theme.ink, margin: 0 }}>
                {batch.total} documents extracted.
              </h2>
              <p style={{ fontFamily: fonts.sans, fontSize: 14, color: theme.inkSoft, lineHeight: 1.5, margin: "8px 0 0 0" }}>
                {flaggedRows.length} value{flaggedRows.length !== 1 ? "s" : ""} need a human eye. The rest are ready to export.
              </p>
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {onIntelligence && (
                <QuietButton theme={theme} fonts={fonts} onClick={onIntelligence}>View intelligence →</QuietButton>
              )}
              {flaggedRows.length > 0 && (
                <QuietButton theme={theme} fonts={fonts} onClick={onReviewQueue} style={{ borderColor: theme.confidenceLow, color: theme.confidenceLow }}>
                  Review queue ({flaggedRows.length})
                </QuietButton>
              )}
              <QuietButton theme={theme} fonts={fonts} primary onClick={onExport}>Export all</QuietButton>
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 48px 32px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ border: `1px solid ${theme.rule}`, background: theme.docPaper }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: fonts.sans, fontSize: 13, color: theme.ink }}>
              <thead style={{ position: "sticky", top: 0, background: theme.surface, zIndex: 1 }}>
                <tr>
                  <th style={thStyle(theme, fonts)}>Document</th>
                  <th style={thStyle(theme, fonts)}>Insured</th>
                  <th style={thStyle(theme, fonts)}>Loss type</th>
                  <th style={{ ...thStyle(theme, fonts), textAlign: "right" }}>Amount</th>
                  <th style={thStyle(theme, fonts)}>Date</th>
                  <th style={{ ...thStyle(theme, fonts), textAlign: "right", width: 100 }}>Avg. conf.</th>
                  <th style={{ ...thStyle(theme, fonts), width: 80 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {batch.failed > 0 && (
                  <tr style={{ borderTop: `1px solid ${theme.rule}`, background: `${theme.confidenceLow}08` }}>
                    <td colSpan={7} style={{ padding: "10px 14px", fontFamily: fonts.sans, fontSize: 12, color: theme.confidenceLow }}>
                      <span style={{ fontFamily: fonts.mono, fontSize: 11 }}>⚠</span>
                      {" "}<strong>{batch.failed} document{batch.failed !== 1 ? "s" : ""} could not be read.</strong> Marked below — open to retry or replace.
                    </td>
                  </tr>
                )}
                {batch.rows.map((r, i) => (
                  <tr key={i} onClick={() => onRowClick?.(r, i)} style={{ borderTop: `1px solid ${theme.rule}`, cursor: "pointer", background: r.failed ? `${theme.confidenceLow}06` : "transparent", opacity: r.failed ? 0.85 : 1 }}>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: fonts.mono, fontSize: 12, color: theme.ink }}>{r.file}</span>
                      {r.failReason && (
                        <div style={{ fontFamily: fonts.sans, fontSize: 11, color: theme.confidenceLow, marginTop: 3 }}>{r.failReason}</div>
                      )}
                    </td>
                    <td style={tdStyle}>{r.failed ? <span style={{ color: theme.inkMute }}>—</span> : r.insured}</td>
                    <td style={{ ...tdStyle, color: theme.inkSoft }}>{r.failed ? "—" : r.type}</td>
                    <td style={{ ...tdStyle, textAlign: "right", fontFamily: fonts.mono }}>
                      {r.failed ? <span style={{ color: theme.inkMute }}>—</span> : r.amount}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: fonts.mono, color: theme.inkSoft }}>{r.failed ? "—" : r.date}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {r.failed ? (
                        <span style={{ color: theme.inkMute }}>—</span>
                      ) : (
                        <span style={{ fontFamily: fonts.mono, fontSize: 11, color: r.confidence < 0.85 ? theme.confidenceLow : theme.inkSoft }}>
                          {Math.round(r.confidence * 100)}%
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {r.failed ? (
                        <span style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.confidenceLow, letterSpacing: "0.06em", textTransform: "uppercase" }}>✕ Failed</span>
                      ) : r.flagged || r.confidence < 0.85 ? (
                        <span style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.confidenceLow, letterSpacing: "0.06em", textTransform: "uppercase" }}>⚑ Review</span>
                      ) : (
                        <span style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkSoft, letterSpacing: "0.06em", textTransform: "uppercase" }}>✓ Ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
